
interface LoginCredentials {
  email: string;
  password: string;
}

interface User {
  email: string;
  nombre: string;
  role: 'admin' | 'user';
}

interface AuthResponse {
  success: boolean;
  token: string;
  user: User;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://alere-cafe-ai-demo-22066.nodechef.com/api';

class AuthService {
  private token: string | null = null;
  private user: User | null = null;

  constructor() {
    // Load token and user from localStorage on initialization
    this.loadFromStorage();
  }

  private loadFromStorage() {
    const token = localStorage.getItem('authToken');
    const userStr = localStorage.getItem('user');
    
    if (token && userStr) {
      this.token = token;
      this.user = JSON.parse(userStr);
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error en el login');
      }

      const data: AuthResponse = await response.json();
      
      // Save token and user
      this.token = data.token;
      this.user = data.user;
      
      // Save to localStorage
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Set default auth header for apiService
      this.setAuthHeader();
      
      return data;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }

  logout() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    
    // Remove auth header
    this.removeAuthHeader();
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  getUser(): User | null {
    return this.user;
  }

  getToken(): string | null {
    return this.token;
  }

  hasRole(role: string): boolean {
    return this.user?.role === role;
  }

  isAdmin(): boolean {
    return this.user?.role === 'admin';
  }

  private setAuthHeader() {
    if (this.token) {
      // Add auth header to all fetch requests
      const originalFetch = window.fetch;
      window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
        init = init || {};
        init.headers = {
          ...init.headers,
          'Authorization': `Bearer ${this.token}`,
        };
        return originalFetch(input, init);
      };
    }
  }

  private removeAuthHeader() {
    // This is a simplified version - in production you might want to handle this differently
    window.location.reload();
  }

  async verifyToken(): Promise<boolean> {
    if (!this.token) return false;

    try {
      const response = await fetch(`${API_URL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });

      if (!response.ok) {
        this.logout();
        return false;
      }

      const data = await response.json();
      this.user = data.user;
      return true;
    } catch (error) {
      console.error('Error verificando token:', error);
      this.logout();
      return false;
    }
  }
}

export const authService = new AuthService();